import { createFileRoute, Link } from "@tanstack/react-router";
import { Breadcrumbs, breadcrumbListJsonLd } from "@/components/site/Breadcrumbs";
import { CTASection } from "@/components/site/CTASection";
import { PageHero } from "@/components/site/PageShell";
import { canonical } from "@/components/site/site-config";

const TITLE = "Roof Cost in Citrus County, FL (2026 Prices) | Destination Roofing";
const DESCRIPTION = "See Destination Roofing's 2026 Citrus County price ranges for metal, shingle, mobile home roof-over, coating and leak repair work.";
const PATH = "/roof-cost-citrus-county";

const PRICE_ROWS = [
  ["Standing seam metal", "$800 to $1,000", "per square"],
  ["Exposed fastener metal", "$425 to $650", "per square"],
  ["Architectural shingle", "$350 to $450", "per square"],
  ["Mobile home metal roof-over", "$6,500 to $8,500", "typical whole job"],
  ["Mobile home TPO roof-over", "$4,500 to $7,000", "typical whole job"],
  ["Roof coating", "about $250", "per square"],
  ["Leak repair", "$500 to $2,500", "typical job"],
];

export const Route = createFileRoute("/roof-cost-citrus-county")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { name: "robots", content: "index,follow" },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: canonical(PATH) },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: canonical(PATH) }],
    scripts: [{
      type: "application/ld+json",
      children: JSON.stringify(
        breadcrumbListJsonLd([
          
          { label: "Roof Cost in Citrus County", href: PATH },
        ]),
      ),
    }],
  }),
  component: RoofCostCitrusCountyPage,
});

function RoofCostCitrusCountyPage() {
  return (
    <>
      <Breadcrumbs items={[{ label: "Roof Cost in Citrus County", href: PATH }]} />
      <PageHero
        eyebrow="Homeowner Guide"
        title="How Much Does a New Roof Cost in Citrus County, FL? (2026)"
        subtitle="2026 installed price ranges from Destination Roofing jobs in Citrus County."
      />
      <main className="mx-auto max-w-4xl px-4 py-12 md:py-16">
        <article className="prose prose-slate max-w-none">
          <p className="text-lg leading-relaxed">
            In 2026, Destination Roofing&apos;s installed prices in Citrus County run about $800 to $1,000 per square for standing seam metal, $425 to $650 per square for exposed fastener (screw-down) metal and $350 to $450 per square for architectural shingles. A roofing square is 100 square feet of roof surface. Your exact price comes from a free roof inspection.
          </p>

          <h2>2026 price ranges from our Citrus County jobs</h2>
          <div className="not-prose my-6 overflow-x-auto rounded-lg border border-border">
            <table className="w-full min-w-[38rem] border-collapse text-left text-sm">
              <thead className="bg-muted">
                <tr>
                  <th scope="col" className="px-4 py-3 font-semibold text-foreground">Roof type</th>
                  <th scope="col" className="px-4 py-3 font-semibold text-foreground">Price range</th>
                  <th scope="col" className="px-4 py-3 font-semibold text-foreground">Unit</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border bg-card">
                {PRICE_ROWS.map(([type, price, unit]) => (
                  <tr key={type}>
                    <th scope="row" className="px-4 py-3 font-medium text-foreground">{type}</th>
                    <td className="px-4 py-3 text-muted-foreground">{price}</td>
                    <td className="px-4 py-3 text-muted-foreground">{unit}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p>Ranges are based on Destination Roofing jobs as of September 2026 and are not a quote.</p>

          <h2>What a roofing square means</h2>
          <p>
            One square equals 100 square feet of roof surface. Roof surface is larger than the home&apos;s floor area because of slope and overhangs, so we measure the roof itself.
          </p>

          <h2>Example: a 20-square roof</h2>
          <p><strong>Example only:</strong></p>
          <ul>
            <li>20 squares of standing seam metal at $800 to $1,000 per square is about $16,000 to $20,000.</li>
            <li>20 squares of exposed fastener metal at $425 to $650 is about $8,500 to $13,000.</li>
            <li>20 squares of architectural shingle at $350 to $450 is about $7,000 to $9,000.</li>
          </ul>

          <h2>What moves the price up or down</h2>
          <ul>
            <li>Roof size in squares</li>
            <li>Roof pitch and complexity</li>
            <li>How many layers must be torn off</li>
            <li>Any rotted decking found during tear-off</li>
            <li>The material and color chosen</li>
            <li>Permit requirements</li>
          </ul>

          <h2>Metal vs shingle: the cost side</h2>
          <p>
            In our price ranges, <Link to="/metal-roofing">standing seam metal</Link> costs roughly twice as much per square as <Link to="/shingle-roofing">architectural shingle</Link>, and exposed fastener metal sits in between.
          </p>

          <h2>Mobile home roofs</h2>
          <p>
            Metal roof-overs typically run $6,500 to $8,500 and TPO roof-overs $4,500 to $7,000 for the whole job. Learn about <Link to="/mobile-home-roofing">mobile and manufactured home roofing</Link>.
          </p>

          <h2>Repairs and coatings</h2>
          <p>
            Most leak repairs run $500 to $2,500; roof coatings run about $250 per square. Learn about <Link to="/roof-repair">roof repair</Link>.
          </p>

          <h2>Permits and insurance</h2>
          <p>Learn how the <Link to="/citrus-county-roof-permits">Citrus County re-roof permit process</Link> works.</p>
          <p>Review <Link to="/florida-roof-insurance-rules">Florida roof insurance and code rules</Link> before calling your insurer.</p>

          <h2>Frequently asked questions</h2>
          <h3>How much is a new metal roof in Citrus County?</h3>
          <p>Standing seam metal runs about $800 to $1,000 per square, and exposed fastener metal runs about $425 to $650 per square.</p>
          <h3>How much is a shingle roof?</h3>
          <p>Architectural shingles run about $350 to $450 per square.</p>
          <h3>What is a roofing square?</h3>
          <p>A roofing square is 100 square feet of roof surface.</p>
          <h3>How do I get an exact price?</h3>
          <p>Schedule a free roof inspection; we measure the roof and give a written proposal.</p>

          <p className="text-sm text-muted-foreground"><strong>Updated September 2026</strong></p>
        </article>
      </main>
      <CTASection
        headline="Schedule My Free Roof Inspection"
        sub="We measure the roof and give you a written proposal."
      />
    </>
  );
}